import { useEffect, useState } from "react";
import api from "@/lib/api";
import { History, Plus, Settings2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { dec, eur, pill } from "@/components/excelVoce/format";
import { ContrattoDialog } from "./ContrattoDialog";
import { NuovoSalDialog } from "./NuovoSalDialog";
import { SalDettaglio } from "./SalDettaglio";
import { StoricoSal } from "./StoricoSal";
import { STATO_SAL, canEditSal, isAdmin } from "./labels";

const Box = ({ label, v, testid, cls = "" }) => (
  <div className="rounded-md border p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
    <div className={`font-mono text-sm mt-1 ${cls}`}>{v}</div>
  </div>
);

const Riepilogo = ({ r }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="sal-riepilogo">
    <Box label="Contabilizzato" v={eur(r.contabilizzato)} testid="riep-contabilizzato" cls="font-semibold text-sky-800" />
    <Box label="Detrazioni" v={eur(r.detrazioni)} testid="riep-detrazioni" />
    <Box label="Netto emesso" v={eur(r.netto_emesso)} testid="riep-netto" />
    <Box label="Liquidato" v={eur(r.liquidato)} testid="riep-liquidato" />
    <Box label="Pagato" v={eur(r.pagato)} testid="riep-pagato" cls="text-emerald-700" />
    <Box label="Da pagare" v={eur(r.da_pagare)} testid="riep-da-pagare" />
    <Box label="Anticipazione residua" v={`${eur(r.anticipazione_residua)} / ${eur(r.anticipazione)}`} testid="riep-anticipazione" />
    <Box label="Ritenute trattenute" v={eur(Number(r.ritenuta_garanzia) + Number(r.ritenuta_infortuni))} testid="riep-ritenute" />
  </div>
);

const Condizioni = ({ k }) => (
  <p className="text-xs text-slate-600" data-testid="sal-condizioni">
    Condizioni: anticipazione {eur(k.anticipazione)} con recupero {dec(k.recupero_pct, 0, 2)}% · ritenuta a garanzia {k.ritenuta_garanzia_pct ? `${dec(k.ritenuta_garanzia_pct, 0, 2)}%` : "non prevista"} · 0,5% infortuni {k.infortuni ? "prevista" : "non prevista"} · altre detrazioni {k.altre_detrazioni ? "previste" : "non previste"}{k.riferimento ? ` · rif. ${k.riferimento}` : ""}
  </p>
);

const ListaSal = ({ sal, onOpen }) => (
  <Table data-testid="sal-lista">
    <TableHeader><TableRow><TableHead>Documento</TableHead><TableHead>Periodo</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Lordo periodo</TableHead><TableHead className="text-right">Netto</TableHead><TableHead className="text-right">Liquidato</TableHead></TableRow></TableHeader>
    <TableBody>{sal.map((x) => (
      <TableRow key={x.id} className="cursor-pointer hover:bg-slate-50" onClick={() => onOpen(x.id)} data-testid={`sal-riga-${x.id}`}>
        <TableCell className="font-medium">{x.etichetta}</TableCell>
        <TableCell className="text-xs">{x.periodo_da || "…"} → {x.periodo_a}</TableCell>
        <TableCell><span className={`${pill} ${STATO_SAL[x.stato].cls}`}>{STATO_SAL[x.stato].label}</span></TableCell>
        <TableCell className="text-right font-mono text-xs">{eur(x.lordo_periodo)}</TableCell>
        <TableCell className="text-right font-mono text-xs">{eur(x.netto)}</TableCell>
        <TableCell className="text-right font-mono text-xs">{x.importo_liquidato ? eur(x.importo_liquidato) : "—"}</TableCell>
      </TableRow>))}
      {!sal.length && <TableRow><TableCell colSpan={6} className="text-sm text-slate-500">Nessun SAL.</TableCell></TableRow>}
    </TableBody>
  </Table>
);

const Disponibili = ({ righe }) => (
  <div className="overflow-x-auto" data-testid="sal-disponibili">
    <div className="text-xs font-semibold text-slate-700 mb-1">Quantità verificate ancora da contabilizzare ({righe.length} voci)</div>
    {!righe.length ? <p className="text-xs text-slate-500">Nessuna quantità verificata in attesa.</p> : (
      <Table><TableHeader><TableRow><TableHead>N.</TableHead><TableHead>Voce</TableHead><TableHead>UM</TableHead><TableHead className="text-right">Computo</TableHead><TableHead className="text-right">Già contabilizzata</TableHead><TableHead className="text-right">Da contabilizzare</TableHead></TableRow></TableHeader>
        <TableBody>{righe.map((r) => (
          <TableRow key={r.riga_computo_id} data-testid={`disp-${r.riga_computo_id}`}>
            <TableCell className="text-xs">{r.num_ord}</TableCell><TableCell className="text-xs max-w-md truncate">{r.tariffa} · {r.designazione}</TableCell><TableCell className="text-xs">{r.um}</TableCell>
            <TableCell className="text-right font-mono text-xs">{dec(r.quantita_computo)}</TableCell><TableCell className="text-right font-mono text-xs">{dec(r.contabilizzata)}</TableCell>
            <TableCell className="text-right font-mono text-xs font-semibold">{dec(r.da_contabilizzare)} <span className="text-slate-400">({r.n_quote})</span></TableCell>
          </TableRow>))}</TableBody></Table>)}
  </div>
);

export const ContrattoPanel = ({ kid, onChanged }) => {
  const { user } = useAuth();
  const [k, setK] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get(`/sal-contratti/${kid}`).then((r) => setK(r.data));
  useEffect(() => { load(); }, [kid]);
  const refresh = () => { load(); onChanged(); };
  if (!k) return null;
  const emessi = k.sal.filter((x) => ["EMESSO", "LIQUIDATO"].includes(x.stato));
  return (
    <Card data-testid="sal-contratto-panel"><CardContent className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto text-sm font-semibold" data-testid="sal-contratto-nome">{k.nome}</div>
        <Button size="sm" variant="outline" onClick={() => setDlg("storico")} data-testid="sal-storico-btn"><History className="h-3.5 w-3.5 mr-1" /> Storico</Button>
        {isAdmin(user) && <Button size="sm" variant="outline" onClick={() => setDlg("condizioni")} data-testid="sal-condizioni-btn"><Settings2 className="h-3.5 w-3.5 mr-1" /> Condizioni</Button>}
        {isAdmin(user) && emessi.length > 0 && <Button size="sm" variant="outline" onClick={() => setDlg("rettifica")} data-testid="sal-nuova-rettifica">Documento di rettifica</Button>}
        {canEditSal(user) && <Button size="sm" onClick={() => setDlg("sal")} data-testid="sal-nuovo-sal"><Plus className="h-3.5 w-3.5 mr-1" /> Nuovo SAL</Button>}
      </div>
      <Condizioni k={k} />
      <Riepilogo r={k.riepilogo} />
      <ListaSal sal={k.sal} onOpen={setOpen} />
      <Disponibili righe={k.disponibili} />
      {dlg === "condizioni" && <ContrattoDialog contratto={k} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); refresh(); }} />}
      {(dlg === "sal" || dlg === "rettifica") && <NuovoSalDialog kid={kid} rettifica={dlg === "rettifica"} emessi={emessi} onClose={() => setDlg(null)} onSaved={(x) => { setDlg(null); refresh(); setOpen(x.id); }} />}
      {dlg === "storico" && <StoricoSal kid={kid} onClose={() => setDlg(null)} />}
      {open && <SalDettaglio sid={open} onClose={() => { setOpen(null); refresh(); }} />}
    </CardContent></Card>
  );
};
